/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2019, 2025
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/

import { Route, Routes } from 'react-router';

import ErrorBoundary from '@ibm-watson/shared/modules/ia/components/error-boundary/ErrorBoundary';
import InactivityManager from '@ibm-watson/shared/modules/ia/components/inactivity/InactivityManager';
import InstancesPanel from '@ibm-watson/shared/modules/ia/components/instances/InstancesPanel';
import NotificationsContainer from '@ibm-watson/shared/modules/ia/components/notifications/NotificationsContainer';
import PlanLimitCheck from '@ibm-watson/shared/modules/ia/components/PlanLimitCheck/PlanLimitCheck';
import UpgradeModal from '@ibm-watson/shared/modules/ia/components/upgrade-modal/UpgradeModal';
import NotFound from '@ibm-watson/shared/modules/ia/pages/errors/NotFound';
import Unauthorized from '@ibm-watson/shared/modules/ia/pages/errors/Unauthorized';
import InstancesPage from '@ibm-watson/shared/modules/ia/pages/instances/Instances';
import Login from '@ibm-watson/shared/modules/ia/pages/login/Login';
import PrivateRoute from '@ibm-watson/shared/modules/ia/pages/private-route/PrivateRoute';
import { isWxoEnabled } from '@ibm-watson/shared/modules/ia/util/config';
import IntercomOAuth from '../integrations/IntercomOAuth';
import InstanceRoute from './InstanceRoute';

/**
 * This component renders the main routes of the application, which are rendered
 * after user authentication has been initiated.
 *
 * It renders the component that handles showing toast notifications; the one
 * that checks if an instance has exceeded its plan limits; the one that
 * constantly sends requests to the server to make sure the authentication
 * session has not expired; and the one that shows the modal for upgrading the
 * instance plan.
 *
 * It renders the main routes of the application, such as the login route;
 * the slash or "/instances" route (which shows the list of instances
 * the user has access to); the routes associated with the instance; and the
 * generic routes for when the path is invalid, or the user does not have
 * access to the application.
 *
 * Note that some of the aforementioned routes are not valid, and thus
 * never accessible, if we are rendering under WXO.
 *
 * This component also renders two nodes with well-known IDs that the Panel
 * component uses to anchor itself.
 */

const MainRoutes = () => (
  <ErrorBoundary>
    <div role="complementary" aria-label="Notifications">
      <NotificationsContainer />
    </div>
    {!isWxoEnabled && (
      <>
        <InstancesPanel />
        <PlanLimitCheck />
        <InactivityManager />
        <div role="complementary" aria-label="Upgrade">
          <UpgradeModal />
        </div>
      </>
    )}
    <section className="wc--main-container">
      <Routes>
        {!isWxoEnabled && (
          <>
            <Route path="/login" element={<Login />} />
            <Route path="/:instance/login" element={<Login />} />
            {/* This '/oauth' route is related to integrations. It would be better implemented as a server side route. */}
            <Route
              path="/oauth"
              element={<PrivateRoute component={IntercomOAuth} />}
            />
            <Route
              path="/"
              element={<PrivateRoute component={InstancesPage} />}
            />
            <Route
              path="/instances"
              element={<PrivateRoute component={InstancesPage} />}
            />
          </>
        )}
        <Route path="/unauthorized" element={<Unauthorized />} />
        <Route
          path="/:instance/*"
          element={<PrivateRoute component={InstanceRoute} />}
        />
        <Route path="*" element={<NotFound />} />
      </Routes>
    </section>
    {/* #panel-root and full-screen-panel-root are required by the Panel component as an anchor */}
    <div id="panel-root" />
    <div id="full-screen-panel-root" />
  </ErrorBoundary>
);

export default MainRoutes;
